import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { SelectItem, SortMeta } from "primeng/api";

import { rateStatus } from "../../rate/rate-status.const";
import { Rate } from "../../rate/rate-type";
import { containerLoad } from "../../shared/container/load-types.const";
import { quoteStatus } from "../quote-form/quote-status.const";
import { GridParams } from "../../shared/grid/grid-params";
import { rateType } from "../../rate/rate-type.const";
import { rateStructure } from "../../rate/rate-structure.const";
import { GridSortingService } from "../../shared/grid/grid-sorting.service";
import { FclTariff } from "../models/quote-fcl-tariff.model";
import { RateRepository } from "../../rate/rate.repository";
import { TierRepository } from "../../tier/tier.repository";
import { FclTier } from "../../tier/tier-fcl.model";
import { IListWithTiers } from "../interfaces/list-with-tiers.interface";
import { ORDER } from "../../shared/grid/order.enum";
import { FormatContainerLabelPipe } from "../../shared/container/format-container-label.pipe";
import { PaginationList } from "../../shared/grid/pagination-list.model";
import { Subscription } from "rxjs";
import { tableColumn } from "../../ui/table/table-column.const";
import { ITableColumn } from "../../ui/table/table-column.interface";
import { Bind } from "primeng/bind";
import { Select } from "primeng/select";
import { FormsModule } from "@angular/forms";
import { TableComponent } from "../../ui/table/table.component";
import { SearchBoxComponent } from "../../ui/search-box/search-box.component";

@Component({
  selector: "app-quote-contract-list",
  templateUrl: "./quote-contract-list.component.html",
  styleUrls: ["./quote-contract-list.component.scss"],
  providers: [
    GridParams,
    {
      provide: GridParams.DEFAULTS,
      useValue: {
        "criteria[discriminator]": containerLoad.fcl,
        "criteria[type]": rateType.contract,
        "criteria[structure]": rateStructure.linked,
      },
    },
    GridSortingService,
    {
      provide: GridSortingService.SORTING_FIELDS,
      useValue: ["status", "updatedAt"],
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, Select, FormsModule, TableComponent, SearchBoxComponent],
})
export class QuoteContractListComponent
  implements OnInit, OnDestroy, IListWithTiers
{
  gridParams = inject(GridParams);
  protected sortingService = inject(GridSortingService);
  protected rateRepository = inject(RateRepository);
  protected tierRepository = inject(TierRepository);

  public readonly quote = input<FclTariff>(undefined);
  public readonly contractSelect = output<{ rates: Rate[] }>();
  public readonly tierSelect = output<{ tier: FclTier }>();

  public selectedTier: FclTier = null;
  public selectedTierName = "";
  public tierList: SelectItem[] = [];
  public searchQuery = "";
  public rateList: PaginationList<Rate> = new PaginationList<Rate>();
  public selectedRates: Rate[] = [];
  public loading = false;
  public readonly columns = computed<ITableColumn[]>(() => {
    const quote = this.quote();
    const loadColumns = (quote ? quote.getEquipment() : []).map(
      (containerName) => ({
        header: new FormatContainerLabelPipe().transform(containerName),
        field: "totals",
        containerName: containerName,
        type: tableColumn.container,
      }),
    );

    return [
      {
        header: "Status",
        field: "status",
        type: tableColumn.status,
        sortable: true,
      },
      {
        header: "Name",
        field: "name",
        type: tableColumn.default,
        sortable: true,
      },
      {
        header: "Service",
        field: "carrier",
        type: tableColumn.service,
        sortable: true,
      },
      {
        header: "From",
        field: "validFrom",
        type: tableColumn.date,
        sortable: true,
      },
      {
        header: "Till",
        field: "validTill",
        type: tableColumn.date,
        sortable: true,
      },
      {
        header: "Base Origin",
        field: "realOriginLocation",
        type: tableColumn.location,
        sortable: true,
      },
      {
        header: "Base Dest.",
        field: "realDestinationLocation",
        type: tableColumn.location,
        sortable: true,
      },
      ...loadColumns,
      {
        header: "Curr.",
        field: "currency",
        type: tableColumn.currency,
        sortable: true,
      },
      { header: "", field: "internalNote", type: tableColumn.comment },
    ];
  });

  private subscription = new Subscription();
  private fetchSubscription?: Subscription;

  ngOnInit() {
    this.buildTierList();
    this.setTemplateOptions();
    this.setGridParamsOptions();
    this.subscription.add(
      this.gridParams.onChange.subscribe(() => this.fetchRates()),
    );
    this.fetchRates();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public onSelectionChange(rowData: Rate) {
    this.contractSelect.emit({
      rates: this.selectedRates,
    });
  }

  public selectTier(event: {
    originalEvent: MouseEvent;
    value: FclTier;
  }): void {
    this.selectedTier = event.value;
    this.selectedTierName = event.value.getName();
    this.tierSelect.emit({ tier: event.value });
  }

  public setTemplateOptions() {
    this.selectedTier = this.quote().getTier();
    this.selectedRates = this.quote().getRates();
  }

  public buildTierList() {
    this.tierList = [];

    this.subscription.add(
      this.tierRepository.list().subscribe((tierList) => {
        tierList.forEach((tier) => {
          if (tier instanceof FclTier) {
            this.tierList.push({ label: tier.getName(), value: tier });
          }
        });
      }),
    );
  }

  public resetGridParamsOptions() {
    Object.keys(this.gridParams.params).forEach((key) => {
      if (!this.gridParams.isDefaultParam(key)) {
        this.gridParams.unsetParam(key);
      }
    });
  }

  public setGridParamsOptions() {
    const quote = this.quote();
    if (quote.getStatus() === quoteStatus.draft) {
      const client =
        quote.getClients() && quote.getClients().length
          ? quote.getClients()[0].getId()
          : null;
      this.gridParams.setParams({
        "criteria[status]": rateStatus.published,
        "criteria[restrictedClients]": client,
        "criteria[valid]": true,
      });
    } else {
      const rates = quote.getRates();
      for (let i = 0; i < rates.length; i++) {
        this.gridParams.setParam("criteria[id][" + i + "]", rates[i].getId());
      }
    }
  }

  public fetchRates(): void {
    this.fetchSubscription?.unsubscribe();

    this.loading = true;
    this.fetchSubscription = this.rateRepository
      .getPaginatedRateList(this.gridParams.getHttpParams())
      .subscribe({
        next: (rateList) => {
          const quote = this.quote();
          if (quote) {
            this.selectedRates = quote.getRates();
          }
          this.rateList = rateList;
        },
        error: (err) => {},
        complete: () => {
          this.loading = false;
        },
      });
    this.subscription.add(this.fetchSubscription);
  }

  public loadPage(page: number, rows: number) {
    this.gridParams.setParams({
      page: page,
      limit: rows,
    });
  }

  public sortGrid({ field, order }: SortMeta) {
    this.sortingService.sortGrid(field, ORDER[order]);
  }

  public onSearchQueryChange() {
    this.gridParams.setParams({
      page: 1,
      search: this.searchQuery || "",
    });
  }
}
